<template>
    <v-container grid-list-xs>
        <div>
            <v-row>
                <v-col>
                    <v-sheet class="pa-5" elevation="3">
                        <h4 class="mb-2 primary--text">Request a credit limit increase</h4>
                        <ul class="mb-1">
                            <li>Credit Line: {{ creditLimitCents | currency }}</li>
                            <li>Available Credit: {{ availableAmountCents | currency }}</li>
                            <li>Treatment Total: {{ treatmentAmountCents | currency }}</li>
                        </ul>
                        <p class="mb-5">
                            Request a new credit limit below. You will need to increase your credit limit to at least
                            <strong>{{ recommendedAmountCents | currency }}</strong> to fully cover your treatment.
                        </p>
                        <p><small>New requested credit limit:</small></p>
                        <v-row>
                            <v-col>
                                <div v-show="showRecommended" class="recommended-credit-limit-box">
                                    <strong>{{ recommendedAmountCents | currency }}</strong>
                                    <small>(recommended)</small>
                                </div>

                                <PaymentPlanEstimator
                                    v-show="!showRecommended"
                                    ref="estimator"
                                    :value="calculatorAmountDollars"
                                    :max="maxCalculatorValue"
                                    :min="minCalculatorValue"
                                    :step="calculatorStep"
                                    class="credit-limit-field"
                                    @step="handleCalculatorStepped"
                                    @type="handleCalculatorTyped"
                                    @input="handleCalculatorInput"
                                />
                            </v-col>
                        </v-row>
                        <p class="pt-1">
                            <small>
                                <a
                                    href=""
                                    style="text-decoration: none"
                                    :class="{
                                        'error--text':
                                            newCreditLimitCents < creditLimitCents + 5000 && !showRecommended,
                                    }"
                                    @click.prevent="toggleRecommended()"
                                >
                                    {{ recommendedLabel }}
                                </a>
                            </small>
                        </p>
                        <v-row justify="center" class="mt-12">
                            <v-col>
                                <BackButton class="mr-4" color="primary" rounded />
                            </v-col>

                            <v-col class="text-right">
                                <Button class="secondary" rounded :disabled="continueDisabled" @click="next">
                                    {{ $t("action.continue") }}
                                </Button>
                            </v-col>
                        </v-row>
                    </v-sheet>
                </v-col>
            </v-row>
        </div>
    </v-container>
</template>

<script>
    import { sessionState } from "@/state"
    import { mapState } from "vuex"
    import accountState from "@/state/AccountState"
    import { centsToDollars } from "@/util/currency"

    export default {
        data() {
            return {
                providerInfo: {},
                showRecommended: true,
                newCreditLimitCents: 0,
                calculatorStep: 500,
                maxCalculatorValue: 100000,
            }
        },

        computed: {
            calculatorAmountDollars() {
                return this.newCreditLimitCents / 100
            },

            ...mapState("application", {
                treatmentAmountCents: state => state.data.purchaseAmountCents || 0,
            }),

            ...mapState("account", {
                availableAmountCents: state => state.data.availableAmountCents || 0,
                creditLimitCents: state => state.data.creditLimitCents || 0,
            }),

            minCalculatorValue() {
                return centsToDollars(this.creditLimitCents + 5000)
            },

            diffAmountCents() {
                return Math.max(0, this.treatmentAmountCents - this.availableAmountCents)
            },

            recommendedLabel() {
                if (this.showRecommended) {
                    return "Adjust amount"
                }
                if (this.newCreditLimitCents < this.creditLimitCents + 5000) {
                    return "Reset to recommended (Must be at least $50 over current credit line)"
                }
                return "Reset to recommended"
            },

            recommendedAmountCents() {
                // at least $50 (5,000 cents) over their current limit
                return this.creditLimitCents + Math.max(5000, this.diffAmountCents)
            },

            continueDisabled() {
                return this.newCreditLimitCents < this.creditLimitCents + 5000
            },
        },

        created() {
            this.newCreditLimitCents = this.recommendedAmountCents
        },

        methods: {
            handleCalculatorInput(value) {
                this.newCreditLimitCents = value * 100
            },

            handleCalculatorTyped(value) {
                this.newCreditLimitCents = value * 100
            },

            handleCalculatorStepped(value) {
                this.$refs.estimator.blur()
                this.newCreditLimitCents = value * 100
            },

            toggleRecommended() {
                this.showRecommended = !this.showRecommended

                if (this.showRecommended) {
                    this.newCreditLimitCents = this.recommendedAmountCents
                }
            },

            cancel() {
                sessionState.openCancelModal()
            },

            next() {
                accountState.account = {
                    ...accountState.account,
                    newCreditLimitCents: this.newCreditLimitCents,
                }
                this.$router.push({ path: `/providers/${sessionState.providerKey}/credit_limit_change/income` })
            },
        },
    }
</script>

<style lang="scss" scoped>
    .recommended-credit-limit-box {
        border: 1px solid #a7adb0;
        border-radius: 4px;
        background-color: #f2f3f4;
        padding: 15px;
        max-width: 270px;
    }
</style>
